import { useState } from "react";

import { PAGINATION } from "manager/config/constants";
import { useGetDealsToMove } from "manager/hooks/api";
import { DealToMove } from "manager/lib/dealsApi";
import { moveApplicationToAnotherDeal } from "shared/lib/api/applicationApi";

import { useRemovePrimaryYardiExtension } from "../hooks";

import { MoveApplicantModalProps, MoveApplicantModalForm } from "./component";

import { OnMovedApplicant } from "./context";

export const useHook = ({
  application,
  dealId,
  yardi,
  enabled = true,
}: MoveApplicantModalProps & { enabled: boolean }) => {
  const [destinationDeal, setDestinationDeal] = useState<Partial<DealToMove>>(
    {}
  );
  const [searchingValue, setSearchingValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(PAGINATION.DEALS_TO_MOVE_PAGE_SIZE);

  const {
    isLoading: isDealsLoading,
    deals,
    totalCount,
    pageSize: responsePageSize,
  } = useGetDealsToMove({
    dealId,
    enabled,
    pagination: {
      page: currentPage,
      pageSize,
    },
    search: searchingValue || undefined,
  });

  const move = async (
    result: MoveApplicantModalForm
  ): Promise<OnMovedApplicant> => {
    await moveApplicationToAnotherDeal({
      applicationId: application.id,
      dealId: destinationDeal.id,
      ...result.extraParams,
    });
    return { dealId: destinationDeal.id };
  };

  const { extension } = useRemovePrimaryYardiExtension(yardi);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (value: string) => {
    // Reset pagination to first page when search changes
    setCurrentPage(1);
    setSearchingValue(value);
  };

  // Calculate more robust pagination data
  const actualPageSize = responsePageSize || pageSize;
  const actualTotalCount = totalCount || 0;
  const totalPages = Math.ceil(actualTotalCount / actualPageSize);
  const hasPagination = actualTotalCount > actualPageSize || currentPage > 1;
  const hasMultiplePages = totalPages > 1;

  // Ensure current page doesn't exceed total pages
  const safePage = Math.min(currentPage, Math.max(1, totalPages));

  return {
    move,
    loading: isDealsLoading,
    deals,
    extension,
    setDestinationDeal,
    setSearchingValue: handleSearchChange,
    // Pagination props
    currentPage: safePage,
    pageSize: actualPageSize,
    totalCount: actualTotalCount,
    totalPages,
    hasPagination,
    hasMultiplePages,
    handlePageChange,
  };
};
